/**
 * TabControl Component
 * Dashboard tab navigation with drag-and-drop reordering
 * Migrated from legacy TabControl.js
 */

import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent, Modifier } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import ActionMenuTab from './ActionMenuTab';
import RenameInput from './RenameInput';

import { Modal } from '@/components/utils/Modal';
import CorgeeStorageHelper from '@/components/Globals/CorgeeStorageHelper';
import { dashboardApi } from '@/services/dashboardApi';
import { dashboardService } from '@/services/dashboardService';
import { getPersonId, getBookmarksConfigValueId } from '@/components/utils/CorgeeHelper';
import type { TabControlProps, Dashboard, ReactFunctions } from './types';
import { getCardBeingDragged, getGroupBeingDragged } from '@/lib/cardDragState';
import { conversationBus } from '@/lib/conversationBus';
import { useToast } from '@/components/ui/Toast';
import './TabControl.css';

// Sortable Tab Item Component
interface SortableTabItemProps {
  item: Dashboard;
  toggleState: number;
  config: TabControlProps['config'];
  dashboards: Dashboard[];
  isHighlighted?: boolean;
  onToggleTab: (event: React.MouseEvent, index: number, dashboard: Dashboard) => void;
  onShowRenameDashboard: (event: React.MouseEvent, dashboardId: number) => void;
  onPinDashboard: (event: React.MouseEvent, dashboard: Dashboard) => void;
  onOpenTabDashboard: (event: React.MouseEvent, dashboard: Dashboard) => void;
  scrollRight: () => void;
  saveRenameDashboard: (
    event: React.MouseEvent | React.KeyboardEvent,
    dashboardId: number,
    text: string
  ) => void;
  closeRename: () => void;
  ifjMoveToModal: (index: number, dashboard: Dashboard) => void;
  setPinedDashboards: (value: React.SetStateAction<Dashboard[]>) => void;
  setNotPinedDashboards: (value: React.SetStateAction<Dashboard[]>) => void;
  onSelectDashboard: (dashboard: Dashboard) => void;
  tabsToShow: Dashboard[];
}

const SortableTabItem: React.FC<SortableTabItemProps> = ({
  item,
  toggleState,
  config,
  dashboards,
  isHighlighted,
  onToggleTab,
  onShowRenameDashboard,
  onPinDashboard,
  onOpenTabDashboard,
  scrollRight,
  saveRenameDashboard,
  closeRename,
  ifjMoveToModal,
  setPinedDashboards,
  setNotPinedDashboards,
  onSelectDashboard,
  tabsToShow,
}) =>
{
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.DashboardId.toString(),
  });

  // Horizontal only: the tab strip is a single row
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform ? { ...transform, y: 0, scaleY: 1 } : null),
    transition,
    // Placeholder while the DragOverlay clone follows the pointer
    opacity: isDragging ? 0.35 : 1,
  };

  const isActive = toggleState === item.DashboardId;
  const isFakeDashboard = item.FakeDashboardType === 1;
  const [isCardDragOver, setIsCardDragOver] = useState(false);
  const [isCopyDragOver, setIsCopyDragOver] = useState(false);
  const [isGroupDragOver, setIsGroupDragOver] = useState(false);

  // Refs to always have the latest values inside native event listeners (avoid stale closures)
  const itemRef = useRef(item);
  const configRef = useRef(config);
  itemRef.current = item;
  configRef.current = config;

  // Native card/group drop listeners. dnd-kit's node ref goes on the <ul>,
  // the element the sort transform is applied to.
  const liRef = useRef<HTMLLIElement | null>(null);

  // Drag-to-tab: detect when a react-dnd card is released over this tab.
  //
  // Two-path design:
  //   Primary  — native 'drop' event on the li (fires when browser considers it a valid target).
  //   Backup   — 'corgee:card-drag-end' dispatched by SortableCard's useDrag end() callback.
  //
  // Why 'dragleave' uses a delayed clear (not immediate):
  //   When the user releases a card over the tab but 'drop' is suppressed (react-dnd's global
  //   dragover handler sets dropEffect='none' for non-registered targets), the browser fires
  //   'dragleave' with relatedTarget=null BEFORE 'dragend' on the source. An immediate clear
  //   would wipe pendingDrop before the backup path can act. Delaying 100 ms lets either 'drop'
  //   or 'corgee:card-drag-end' arrive first and cancel the timer. If neither arrives within
  //   100 ms the user genuinely moved away, so we clear then.
  useEffect(() =>
  {
    const el = liRef.current;
    if (!el)
    {
      return;
    }

    let pendingDrop: {
      conversationId: number;
      groupId: number;
      sourceDashboardId: number;
      isCopy: boolean;
    } | null = null;

    let pendingGroupDrop: {
      sourceGroupId: number;
      sourceDashboardId: number;
      isCopy: boolean;
    } | null = null;

    let leaveTimer: ReturnType<typeof setTimeout> | null = null;

    const CancelLeaveTimer = () =>
    {
      if (leaveTimer !== null)
      {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }
    };

    const CommitDrop = (conversationId: number, groupId: number, sourceDashboardId: number, isCopy: boolean) =>
    {
      CancelLeaveTimer();
      pendingDrop = null;
      setIsCardDragOver(false);
      setIsCopyDragOver(false);
      const cb = configRef.current.functions?.onCardDroppedOnTab;
      if (cb)
      {
        cb(itemRef.current, conversationId, groupId, sourceDashboardId, isCopy);
      }
    };

    const CommitGroupDrop = (sourceGroupId: number, sourceDashboardId: number, isCopy: boolean) =>
    {
      CancelLeaveTimer();
      pendingGroupDrop = null;
      setIsGroupDragOver(false);
      setIsCopyDragOver(false);
      const cb = configRef.current.functions?.onGroupDroppedOnTab;
      if (cb)
      {
        cb(itemRef.current, sourceGroupId, sourceDashboardId, isCopy);
      }
    };

    const OnDragEnter = (e: DragEvent) =>
    {
      // --- Group drag ---
      const draggedGroup = getGroupBeingDragged();
      if (draggedGroup)
      {
        if (draggedGroup.isDefault) return; // dynamic groups cannot be moved
        if (draggedGroup.dashboardId === itemRef.current.DashboardId) return; // same dashboard
        CancelLeaveTimer();
        pendingGroupDrop = {
          sourceGroupId: draggedGroup.groupId,
          sourceDashboardId: draggedGroup.dashboardId,
          isCopy: e.ctrlKey,
        };
        setIsGroupDragOver(true);
        setIsCopyDragOver(e.ctrlKey);
        return;
      }

      // --- Card drag ---
      const dragged = getCardBeingDragged();
      if (!dragged || dragged.dashboardId === itemRef.current.DashboardId)
      {
        return;
      }

      // 1. Validation for ProjectDetails:
      if (configRef.current.projectId && configRef.current.projectId !== 0)
      {
        if (dragged.conversation && dragged.conversation.ProjectId !== configRef.current.projectId)
        {
          return;
        }
      }

      // 2. Validation for CardType or Projects:
      const cvId = configRef.current.configValueId;
      if (cvId !== undefined && cvId !== null && cvId !== 1 && cvId !== getBookmarksConfigValueId())
      {
        if (dragged.conversation && dragged.conversation.ConfigValueId !== cvId)
        {
          return;
        }
      }

      CancelLeaveTimer();
      pendingDrop = {
        conversationId: dragged.conversationId,
        groupId: dragged.groupId,
        sourceDashboardId: dragged.dashboardId,
        isCopy: e.ctrlKey,
      };
      setIsCardDragOver(true);
      setIsCopyDragOver(e.ctrlKey);
    };

    const OnDragOver = (e: DragEvent) =>
    {
      if (!pendingDrop && !pendingGroupDrop)
      {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const isCopy = e.ctrlKey;
      if (e.dataTransfer)
      {
        e.dataTransfer.dropEffect = isCopy ? 'copy' : 'move';
      }
      if (pendingDrop) pendingDrop.isCopy = isCopy;
      if (pendingGroupDrop) pendingGroupDrop.isCopy = isCopy;
      setIsCopyDragOver(isCopy);
    };

    const OnDragLeave = (e: DragEvent) =>
    {
      if (el.contains(e.relatedTarget as Node | null))
      {
        return;
      }
      if (!pendingDrop && !pendingGroupDrop)
      {
        return;
      }
      leaveTimer = setTimeout(() =>
      {
        pendingDrop = null;
        pendingGroupDrop = null;
        leaveTimer = null;
        setIsCardDragOver(false);
        setIsGroupDragOver(false);
        setIsCopyDragOver(false);
      }, 100);
    };

    const OnDrop = (e: DragEvent) =>
    {
      e.preventDefault();
      if (pendingGroupDrop)
      {
        const { sourceGroupId, sourceDashboardId, isCopy } = pendingGroupDrop;
        CommitGroupDrop(sourceGroupId, sourceDashboardId, e.ctrlKey || isCopy);
        return;
      }
      if (!pendingDrop)
      {
        return;
      }
      const { conversationId, groupId, sourceDashboardId, isCopy } = pendingDrop;
      CommitDrop(conversationId, groupId, sourceDashboardId, e.ctrlKey || isCopy);
    };

    const OnCardDragEnd = () =>
    {
      if (!pendingDrop)
      {
        return;
      }
      const { conversationId, groupId, sourceDashboardId, isCopy } = pendingDrop;
      CommitDrop(conversationId, groupId, sourceDashboardId, isCopy);
    };

    const OnGroupDragEnd = () =>
    {
      if (!pendingGroupDrop)
      {
        return;
      }
      const { sourceGroupId, sourceDashboardId, isCopy } = pendingGroupDrop;
      CommitGroupDrop(sourceGroupId, sourceDashboardId, isCopy);
    };

    el.addEventListener('dragenter', OnDragEnter);
    el.addEventListener('dragover', OnDragOver);
    el.addEventListener('dragleave', OnDragLeave);
    el.addEventListener('drop', OnDrop);
    window.addEventListener('corgee:card-drag-end', OnCardDragEnd);
    window.addEventListener('corgee:group-drag-end', OnGroupDragEnd);
    return () =>
    {
      CancelLeaveTimer();
      el.removeEventListener('dragenter', OnDragEnter);
      el.removeEventListener('dragover', OnDragOver);
      el.removeEventListener('dragleave', OnDragLeave);
      el.removeEventListener('drop', OnDrop);
      window.removeEventListener('corgee:card-drag-end', OnCardDragEnd);
      window.removeEventListener('corgee:group-drag-end', OnGroupDragEnd);
    };
  }, []);

  const getClassName = () =>
  {
    let className = 'tab-item';
    if (isHighlighted) className += ' tab-order-changed';
    if (isCardDragOver && toggleState !== item.DashboardId)
    {
      className += isCopyDragOver ? ' tab-card-copy-over' : ' tab-card-drag-over';
    }
    if (isGroupDragOver && toggleState !== item.DashboardId)
    {
      className += isCopyDragOver ? ' tab-card-copy-over' : ' tab-group-drag-over';
    }
    if (isActive && isFakeDashboard)
    {
      className += ' active-tabs dashboard-fake';
    }
    else if (isActive)
    {
      className += ' active-tabs';
    }
    return className;
  };

  return (
    <ul ref={setNodeRef} className="tab-container" style={style}>
      <li
        ref={liRef}
        data-dashboard-id={item.DashboardId}
        className={getClassName()}
        onClick={e => onToggleTab(e, item.DashboardId, item)}
        onDoubleClick={e => onShowRenameDashboard(e, item.DashboardId)}
        {...attributes}
        {...listeners}
      >
        {!item.showEditName && <div className="tab-title">{item.Name}</div>}

        {item.showEditName && (
          <RenameInput
            functions={config.functions}
            config={config}
            inputText={item.Name}
            dashboard={item}
            saveRenameDashboard={saveRenameDashboard}
            closeRename={closeRename}
          />
        )}

        {(item.IsPineableDashboard || item.IsPinedDashboard) && !item.showEditName && (
          <i
            onClick={e =>
            {
              e.stopPropagation();
              onPinDashboard(e, item);
            }}
            className={
              item.IsPineableDashboard
                ? 'fa fa-thumb-tack io-separate-tabs unpinned'
                : 'fa fa-thumb-tack io-separate-tabs'
            }
          ></i>
        )}

        {(item.FakeDashboardType === 0 ||
          item.FakeDashboardType === 4 ||
          item.FakeDashboardType === 5) &&
        !config.recycleBinDashboard &&
        !item.showEditName &&
        !config.isBookmarks &&
        !(
          config.isDashboardShared &&
          item.DashboardId === config.dashboardSharedId &&
          !item.IsPublic
        ) ? (
            <ActionMenuTab
              typeMenu="1"
              openTabDashboard={onOpenTabDashboard}
              scrollRight={scrollRight}
              functions={config.functions}
              config={config}
              canEdit={config.canEdit}
              isAdmin={config.isAdmin}
              api={config.api}
              dashboard={item}
              ifjMoveToModal={ifjMoveToModal}
              dashboards={dashboards}
              onToggleTab={onToggleTab}
              setPinedDashboards={setPinedDashboards}
              setNotPinedDashboards={setNotPinedDashboards}
              activeDashboard={toggleState}
              tabsToShow={tabsToShow}
              onSelectDashboard={onSelectDashboard}
              onDashboardUpdated={config.functions?.onDashboardUpdated}
            />
          ) : (
            <span style={{ paddingRight: '9px' }}></span>
          )}
      </li>
    </ul>
  );
};

// Main TabControl Component
const TabControl: React.FC<TabControlProps> = ({ config }) =>
{
  const [pinedDashboards, setPinedDashboards] = useState<Dashboard[]>([]);
  const [notPinedDashboards, setNotPinedDashboards] = useState<Dashboard[]>([]);
  const [toggleState, setToggleState] = useState<number>(config?.DashboardId || 0);
  const [distance] = useState(600);
  const [showArrows, setShowArrows] = useState(false);
  const [styleInitialNoMore, setStyleInitialNoMore] = useState(true);
  const [styleFinalNoMore, setStyleFinalNoMore] = useState(false);
  const [tabsReady, setTabsReady] = useState(false);
  const [showConfirmationND, setShowConfirmationND] = useState(false);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [highlightedTabIds, setHighlightedTabIds] = useState<Set<number>>(new Set());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  // Set on drag reorder so the layout effect keeps the current scroll position
  const suppressAutoScrollRef = useRef(false);
  const highlightClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { success: toastSuccess } = useToast();

  // Ref to snapshot pinedDashboards inside reloadDashboard without adding it as a dep
  const pinedDashboardsRef = useRef<Dashboard[]>([]);
  useEffect(() =>
  {
    pinedDashboardsRef.current = pinedDashboards;
  }, [pinedDashboards]);

  // Ref so scrollToActiveTab always reads the latest toggleState without being recreated
  const toggleStateRef = useRef(toggleState);
  toggleStateRef.current = toggleState;

  const tabReactRef = useRef<HTMLDivElement>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutClickRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Compile dashboard data
  const compileData = useCallback(
    (dashboards: Dashboard[]) =>
    {
      const pin: Dashboard[] = [];
      const notpin: Dashboard[] = [];

      let filteredDashboards = dashboards;
      if (config.isOrganize)
      {
        filteredDashboards = dashboards.filter(x => x.DashboardId === config.DashboardId);
      }

      const personId = CorgeeStorageHelper.personId();

      for (let index = 0; index < filteredDashboards.length; index++)
      {
        const dashboard = { ...filteredDashboards[index]!, id: index } as Dashboard;

        if (config.isOrganize)
        {
          dashboard.IsOpenDashboard = true;
        }

        if (config.isProjectDetail && dashboard.DashboardId === config.DashboardId)
        {
          dashboard.IsOpenDashboard = true;
        }

        if (
          parseInt(String(dashboard.CreatedById)) !== 0 &&
          parseInt(String(dashboard.CreatedById)) === personId
        )
        {
          dashboard.showDeleteDashboard = true;
        }
        else
        {
          dashboard.showDeleteDashboard = false;
        }

        if (parseInt(String(dashboard.PersonId)) === personId)
        {
          dashboard.IsDashboardCreator = true;
        }
        else
        {
          dashboard.IsDashboardCreator = false;
        }

        if (config.isDashboardShared && dashboard.DashboardId === config.dashboardSharedId)
        {
          if (
            parseInt(String(dashboard.CreatedById)) === personId &&
            !dashboard.IsTabPinedDashboard
          )
          {
            dashboard.IsOpenDashboard = true;
          }
          else if (parseInt(String(dashboard.CreatedById)) !== personId)
          {
            if (config.isProjectDetail)
            {
              // In ProjectDetails: show as an open tab so it's visible, but no pin icon
              dashboard.IsOpenDashboard = true;
            }
            else if (!dashboard.IsPinedDashboard && !dashboard.IsTabPinedDashboard)
            {
              // Only mark pineable if not already pinned, otherwise unpin clicks re-Add instead of Delete
              dashboard.IsPineableDashboard = true;
            }
          }
        }

        if (
          dashboard.IsTabPinedDashboard ||
          dashboard.IsPineableDashboard ||
          dashboard.IsOpenDashboard
        )
        {
          pin.push(dashboard);
        }
        else
        {
          notpin.push(dashboard);
        }
      }

      if (pin.length === 0 && notpin.length > 0)
      {
        const first = notpin.shift()!;
        first.IsOpenDashboard = true;
        pin.push(first);
      }

      // Determine initial dashboard to select
      let currentDashboardId: number = 0;
      let currentDashboard: Dashboard | undefined;

      if (!config.isDashboardShared)
      {
        // Restore last-selected dashboard from C2_currentDashboard using compound key
        try
        {
          const savedList: Array<{
            projectId?: number | null;
            configValueId?: number;
            dashboardId?: number;
            index?: number;
            lastModified?: number;
          }> = JSON.parse(localStorage.getItem('C2_currentDashboard') || '[]');
          const projectIdKey = config.projectId || 0;
          const cvId = config.configValueId;
          // Pick the most recently modified entry for this section (projectId + configValueId)
          const matchingEntries = savedList.filter(
            e => (e.projectId || 0) === projectIdKey && (e.configValueId ?? 1) === cvId,
          );
          const savedEntry =
            matchingEntries.length > 0
              ? matchingEntries.reduce((latest, e) =>
                (e.lastModified ?? 0) > (latest.lastModified ?? 0) ? e : latest,
              )
              : null;

          if (savedEntry)
          {
            // Try by saved dashboardId first
            if (savedEntry.dashboardId)
            {
              currentDashboard = pin.find(x => x.DashboardId === savedEntry.dashboardId);
            }
            // If not found by dashboardId, try by saved index position
            if (
              !currentDashboard &&
              savedEntry.index !== undefined &&
              savedEntry.index >= 0 &&
              savedEntry.index < pin.length
            )
            {
              currentDashboard = pin[savedEntry.index];
            }
          }
        }
        catch
        {
          // ignore localStorage errors
        }

        // Fallback: use config.DashboardId if specified
        if (!currentDashboard && config.DashboardId !== undefined && config.DashboardId !== 0)
        {
          currentDashboard = pin.find(x => x.DashboardId === config.DashboardId);
        }

        // Final fallback: first dashboard in list
        if (!currentDashboard && pin.length > 0)
        {
          currentDashboard = pin[0];
        }

        currentDashboardId = currentDashboard?.DashboardId || 0;
      }
      else
      {
        // Shared dashboard override
        currentDashboardId = config.dashboardSharedId!;
        currentDashboard = pin.find(x => x.DashboardId === config.dashboardSharedId);
        if (!currentDashboard && pin.length > 0)
        {
          currentDashboard = pin[0];
          currentDashboardId = currentDashboard!.DashboardId;
        }
      }

      if (config.functions?.setDashboards)
      {
        config.functions.setDashboards(filteredDashboards, pin, notpin, currentDashboard, true);
      }

      setPinedDashboards(pin);
      setNotPinedDashboards(notpin);
      setToggleState(currentDashboardId);
    },
    [config],
  );

  // Reload dashboard data
  const reloadDashboard = useCallback(
    async (lastPosition?: boolean, dashboardId?: number, isOrderRefresh?: boolean) =>
    {
      // Snapshot current orders before fetching so we can compare after (order refresh only)
      const previousOrders = isOrderRefresh
        ? new Map(pinedDashboardsRef.current.map(d => [d.DashboardId, d.Order ?? 0]))
        : null;

      try
      {
        let dashboards = await dashboardApi.getByConfigValueId(
          config.configValueId,
          config.projectId,
          config.isOrganize,
        );

        if (!dashboards) return;

        dashboards.sort((a, b) => (a.Order > b.Order ? 1 : -1));

        if (config.isOrganize)
        {
          dashboards = dashboards.filter(x => x.DashboardId === config.DashboardId);
        }

        const personId = CorgeeStorageHelper.personId();
        const pin: Dashboard[] = [];
        const notpin: Dashboard[] = [];

        for (let index = 0; index < dashboards.length; index++)
        {
          const dashboard = { ...dashboards[index]!, id: index } as Dashboard;

          if (config.isOrganize)
          {
            dashboard.IsOpenDashboard = true;
          }

          if (
            parseInt(String(dashboard.CreatedById)) !== 0 &&
            parseInt(String(dashboard.CreatedById)) === personId
          )
          {
            dashboard.showDeleteDashboard = true;
          }
          else
          {
            dashboard.showDeleteDashboard = false;
          }

          if (parseInt(String(dashboard.PersonId)) === personId)
          {
            dashboard.IsDashboardCreator = true;
          }
          else
          {
            dashboard.IsDashboardCreator = false;
          }

          if (dashboard.IsTabPinedDashboard)
          {
            pin.push(dashboard);
          }
          else
          {
            notpin.push(dashboard);
          }
        }

        if (pin.length === 0 && notpin.length > 0)
        {
          const first = notpin.shift()!;
          first.IsOpenDashboard = true;
          pin.push(first);
        }

        // A dashboardId is only passed when one was just created (duplicated, or
        // created from the Move/Copy To modal), and a new dashboard belongs at the
        // end of the tab strip. The API can hand it back with the same Order as
        // the dashboard it was copied from, and the sort above then places it
        // arbitrarily - often ahead of its source. lastPosition has its own
        // meaning (select the last tab) so it is excluded here.
        // The new position is persisted the same way a tab drag does it, so the
        // dashboard stays at the end after a page refresh or a socket reload.
        if (!lastPosition && dashboardId !== undefined)
        {
          // Some views (project, item-type) get the new dashboard back without a
          // tab pin, so it would land in the hidden list. Pin it the same way the
          // "show dashboard" menu action does.
          const hiddenIndex = notpin.findIndex(x => x.DashboardId === dashboardId);
          if (hiddenIndex > -1 && !pin.some(x => x.DashboardId === dashboardId))
          {
            const hidden = notpin.splice(hiddenIndex, 1)[0] as Dashboard;
            const newOrder = Math.max(...pin.map(o => o.Order ?? 0), 0) + 1;
            hidden.IsTabPinedDashboard = true;
            hidden.Order = newOrder;
            pin.push(hidden);

            try
            {
              await dashboardService.AddPinedTabDashboard(
                hidden.DashboardId,
                newOrder,
                hidden.ConfigValueId || config.configValueId || 1,
                hidden.ProjectId || 0,
                true,
                config.functions?.getSocketConnectionId?.() || '',
              );
            }
            catch (error)
            {
              console.error('Error pinning duplicated dashboard:', error);
            }
          }

          const movedIndex = pin.findIndex(x => x.DashboardId === dashboardId);
          if (movedIndex > -1)
          {
            const newDashboard = pin[movedIndex]!;
            const needsPersist =
              movedIndex < pin.length - 1 ||
              pin.some(x => x !== newDashboard && (x.Order ?? 0) >= (newDashboard.Order ?? 0));

            if (needsPersist)
            {
              pin.splice(movedIndex, 1);
              pin.push(newDashboard);
              pin.forEach((d, index) =>
              {
                d.Order = index;
              });

              try
              {
                await dashboardApi.updatePosition({
                  dashboards: pin,
                  isFromProject: (config.projectId || 0) > 0,
                  noNotification: false,
                  socketConnectionId: config.functions?.getSocketConnectionId?.() || '',
                });
              }
              catch (error)
              {
                console.error('Error updating dashboard position:', error);
              }
            }
          }
        }

        let currentDashboardId: number;

        if (lastPosition)
        {
          currentDashboardId = pin[pin.length - 1]?.DashboardId || pin[0]?.DashboardId!;
        }
        else if (dashboardId !== undefined)
        {
          currentDashboardId = dashboardId;
        }
        else
        {
          // Use the ref to avoid a stale closure on async reloads (socket refresh).
          const currentToggle = toggleStateRef.current;
          const existDashboard = pin.filter(x => x.DashboardId === currentToggle);
          if (existDashboard.length > 0)
          {
            currentDashboardId = currentToggle;
          }
          else
          {
            currentDashboardId = pin[0]?.DashboardId!;
          }
        }

        const currentDashboard = pin.find(x => x.DashboardId === currentDashboardId) || pin[0];

        if (config.functions?.setDashboards)
        {
          config.functions.setDashboards(dashboards, pin, notpin, currentDashboard, undefined);
        }

        setPinedDashboards(pin);
        setNotPinedDashboards(notpin);
        setToggleState(currentDashboardId);

        // Highlight tabs whose Order changed compared to pre-reload snapshot
        if (isOrderRefresh && previousOrders)
        {
          const changedIds = new Set<number>();
          for (const d of pin)
          {
            const prevOrder = previousOrders.get(d.DashboardId);
            if (prevOrder !== undefined && prevOrder !== (d.Order ?? 0))
            {
              changedIds.add(d.DashboardId);
            }
          }
          if (changedIds.size > 0)
          {
            setHighlightedTabIds(changedIds);
            if (highlightClearTimerRef.current) clearTimeout(highlightClearTimerRef.current);
            highlightClearTimerRef.current = setTimeout(() =>
            {
              setHighlightedTabIds(new Set());
              highlightClearTimerRef.current = null;
            }, 8000);
          }
        }

      }
      catch (error)
      {
        console.error('Error reloading dashboard:', error);
      }
    },
    [config],
  );

  // Open details tab
  const openDetails = useCallback(() =>
  {
    const dashboard = pinedDashboards.find(x => x.Name === 'Details' && x.FakeDashboardType === 1);
    if (dashboard)
    {
      toggleTab(new Event('click') as any, dashboard.DashboardId, dashboard, true);
    }
  }, [pinedDashboards]);

  // Scroll to active tab only if not fully visible, minimum displacement with 20px margin
  const scrollToActiveTab = useCallback(() =>
  {
    requestAnimationFrame(() =>
    {
      requestAnimationFrame(() =>
      {
        const container = tabReactRef.current;
        if (!container) return;

        const activeTab = container.querySelector(
          `[data-dashboard-id="${toggleStateRef.current}"]`,
        ) as HTMLElement | null;
        if (!activeTab) return;

        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const margin = 20;
        const tabRelativeLeft = tabRect.left - containerRect.left;
        const tabRelativeRight = tabRect.right - containerRect.left;

        let newScrollLeft = container.scrollLeft;

        if (tabRelativeLeft < margin)
        {
          newScrollLeft = container.scrollLeft + tabRelativeLeft - margin;
        }
        else if (tabRelativeRight > container.clientWidth - margin)
        {
          newScrollLeft = container.scrollLeft + (tabRelativeRight - container.clientWidth + margin);
        }
        else
        {
          return;
        }

        container.scrollLeft = Math.max(0, newScrollLeft);
      });
    });
  }, []);

  // Force show arrows
  const ForceShowArrows = useCallback(() =>
  {
    checkShowArrows();
  }, []);

  // Check if arrows should be shown by comparing inner content width with container width
  const checkShowArrows = useCallback(() =>
  {
    const container = tabReactRef.current;
    if (!container)
    {
      setShowArrows(false);
      return;
    }
    const inner = container.querySelector('.tab-react-control') as HTMLElement | null;
    const contentWidth = inner ? inner.scrollWidth : 0;
    setShowArrows(contentWidth > container.clientWidth);
  }, []);

  // Re-check arrows and edge states on layout changes (window resize, zoom, sidebar toggle).
  useEffect(() =>
  {
    const container = tabReactRef.current;
    if (!container) return;

    const updateLayout = () =>
    {
      const inner = container.querySelector('.tab-react-control') as HTMLElement | null;
      const hasOverflow = (inner?.scrollWidth ?? 0) > container.clientWidth;
      setShowArrows(hasOverflow);
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setStyleInitialNoMore(scrollLeft <= 0);
      setStyleFinalNoMore(Math.ceil(scrollLeft + clientWidth) >= scrollWidth);
    };

    const observer = new ResizeObserver(updateLayout);
    observer.observe(container);

    window.visualViewport?.addEventListener('resize', updateLayout);

    return () =>
    {
      observer.disconnect();
      window.visualViewport?.removeEventListener('resize', updateLayout);
    };
  }, []);

  // Measure arrows and scroll to active tab after tab list updates
  useLayoutEffect(() =>
  {
    if (pinedDashboards.length === 0) return;

    const container = tabReactRef.current;
    if (!container) return;

    const inner = container.querySelector('.tab-react-control') as HTMLElement | null;
    const hasOverflow = (inner?.scrollWidth ?? 0) > container.clientWidth;
    setShowArrows(hasOverflow);

    const skipAutoScroll = suppressAutoScrollRef.current;
    suppressAutoScrollRef.current = false;

    const activeTab = skipAutoScroll
      ? null
      : (container.querySelector(
        `[data-dashboard-id="${toggleStateRef.current}"]`,
      ) as HTMLElement | null);
    if (activeTab)
    {
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      const margin = 20;
      const tabRelativeLeft = tabRect.left - containerRect.left;
      const tabRelativeRight = tabRect.right - containerRect.left;

      let newScrollLeft = container.scrollLeft;

      if (tabRelativeLeft < margin)
      {
        newScrollLeft = container.scrollLeft + tabRelativeLeft - margin;
      }
      else if (tabRelativeRight > container.clientWidth - margin)
      {
        newScrollLeft = container.scrollLeft + (tabRelativeRight - container.clientWidth + margin);
      }
      else
      {
        newScrollLeft = container.scrollLeft;
      }

      container.scrollLeft = Math.max(0, newScrollLeft);
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setStyleInitialNoMore(scrollLeft <= 0);
    setStyleFinalNoMore(Math.ceil(scrollLeft + clientWidth) >= scrollWidth);

    setTabsReady(true);
  }, [pinedDashboards]);

  // Keep edge states in sync with scroll position
  useEffect(() =>
  {
    const container = tabReactRef.current;
    if (!container) return;

    const updateEdgeStates = () =>
    {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setStyleInitialNoMore(scrollLeft <= 0);
      setStyleFinalNoMore(Math.ceil(scrollLeft + clientWidth) >= scrollWidth);
    };

    container.addEventListener('scroll', updateEdgeStates);
    updateEdgeStates();
    return () => container.removeEventListener('scroll', updateEdgeStates);
  }, []);

  // Reset state when project/section changes
  const isInitializedRef = useRef(false);
  useEffect(() =>
  {
    isInitializedRef.current = false;
    if (tabReactRef.current) tabReactRef.current.scrollLeft = 0;
    setShowArrows(false);
    setStyleInitialNoMore(true);
    setStyleFinalNoMore(false);
    setTabsReady(false);
  }, [config.configValueId, config.projectId]);

  useEffect(() =>
  {
    if (!config || isInitializedRef.current) return;
    isInitializedRef.current = true;

    const fetchData = async () =>
    {
      try
      {
        const dashboards = await dashboardApi.getByConfigValueId(
          config.configValueId,
          config.projectId,
          config.isOrganize,
        );

        if (!dashboards) return;

        dashboards.sort((a, b) => (a.Order > b.Order ? 1 : -1));

        if (config.isDashboardShared)
        {
          const sharedExists =
            dashboards.filter(x => x.DashboardId === config.dashboardSharedId).length > 0;
          if (!sharedExists)
          {
            const sharedDashboard = await dashboardApi.getDashboardById(config.dashboardSharedId!);
            // Always add the shared dashboard regardless of IsPublic so the tab is visible.
            // The page is responsible for showing a "no permission" message when IsPublic is false.
            if (sharedDashboard)
            {
              dashboards.push(sharedDashboard);
            }
          }
        }
        setDashboards(dashboards);
        compileData(dashboards);
      }
      catch (error)
      {
        console.error('Error fetching dashboards:', error);
      }
    };

    fetchData();

    // Event listeners
    window.addEventListener('popstate', scrollToActiveTab);
    window.addEventListener('hashchange', scrollToActiveTab);

    return () =>
    {
      window.removeEventListener('popstate', scrollToActiveTab);
      window.removeEventListener('hashchange', scrollToActiveTab);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.configValueId, config.projectId]);

  // Update dashboard view in internal lists
  const updateDashboardView = useCallback(
    (dashboardId: number, viewTypeId: number, gridSize: number) =>
    {
      setPinedDashboards(prev => prev.map(d =>
        d.DashboardId === dashboardId
          ? { ...d, DashboardViewTypeId: viewTypeId, GridSize: gridSize }
          : d,
      ));

      setNotPinedDashboards(prev => prev.map(d =>
        d.DashboardId === dashboardId
          ? { ...d, DashboardViewTypeId: viewTypeId, GridSize: gridSize }
          : d,
      ));

      setDashboards(prev => prev.map(d =>
        d.DashboardId === dashboardId
          ? { ...d, DashboardViewTypeId: viewTypeId, GridSize: gridSize }
          : d,
      ));
    },
    [],
  );

  // Attach React functions for external access - separate effect
  useEffect(() =>
  {
    if (!config?.functions?.attachReactFunctions) return;

    const reactFunctions: ReactFunctions = {
      reloadDashboard,
      openDetails,
      ForceShowArrows,
      updateDashboardView,
    };
    config.functions.attachReactFunctions(reactFunctions);
  }, [config?.functions, reloadDashboard, openDetails, ForceShowArrows, updateDashboardView]);

  // A dashboard created from another component (e.g. Move/Copy To modal) must show up as a tab
  useEffect(() =>
  {
    const unsubscribe = conversationBus.on('dashboard-created', ({ dashboardId, configValueId }) =>
    {
      if (configValueId !== undefined && configValueId !== config.configValueId) return;
      reloadDashboard(false, dashboardId);
    });
    return unsubscribe;
  }, [config.configValueId, reloadDashboard]);

  // Scroll to the active tab whenever it changes (covers initial load, tab clicks, shared-dashboard selection)
  useEffect(() =>
  {
    if (!toggleState) return;
    scrollToActiveTab();
  }, [toggleState, scrollToActiveTab]);

  // Toggle tab
  const saveTabToStorage = useCallback(
    (dashboardId: number) =>
    {
      try
      {
        const projectIdKey = config.projectId || 0;
        const cvId = config.configValueId;
        const pos = pinedDashboards.findIndex(d => d.DashboardId === dashboardId);

        const savedList: Array<{
          projectId?: number | null;
          configValueId?: number;
          dashboardId?: number;
          index?: number;
          lastModified?: number;
          DashboardViewId?: number;
          GridSize?: number;
        }> = JSON.parse(localStorage.getItem('C2_currentDashboard') || '[]');

        // Find by projectId, configValueId, AND dashboardId to avoid overwriting other dashboards
        const existingIdx = savedList.findIndex(
          e => (e.projectId || 0) === projectIdKey && (e.configValueId ?? 1) === cvId && e.dashboardId === dashboardId,
        );

        if (existingIdx !== -1)
        {
          // Preserve existing fields (DashboardViewId, GridSize) when updating
          savedList[existingIdx] = {
            ...savedList[existingIdx],
            index: pos >= 0 ? pos : 0,
            lastModified: Date.now(),
          };
        }
        else
        {
          // Create new entry for new dashboard
          savedList.push({
            projectId: projectIdKey,
            configValueId: cvId,
            dashboardId,
            index: pos >= 0 ? pos : 0,
            lastModified: Date.now(),
          });
        }

        localStorage.setItem('C2_currentDashboard', JSON.stringify(savedList));
      }
      catch
      {
        // ignore localStorage errors
      }
    },
    [config, pinedDashboards],
  );

  const toggleTab = useCallback(
    (
      event: React.MouseEvent | Event,
      index: number,
      dashboard: Dashboard,
      isFromAngular?: boolean,
    ) =>
    {
      if (isFromAngular)
      {
        config.functions?.setDashboard?.(dashboard);
        setToggleState(index);
        saveTabToStorage(index);
        return;
      }

      const target = event.target as HTMLElement;
      const className = target.className || '';

      // Skip if clicking on action menu elements
      if (
        className.includes('corgee_com_card-action-menu') ||
        className.includes('threepoints-tabs') ||
        className.includes('dropdown-toggle')
      )
      {
        return;
      }

      // Skip if rename input is open
      if (document.querySelector('.editable-container-tabs'))
      {
        return;
      }

      // Skip if clicking on pin icon
      if (className.includes('fa-thumb-tack'))
      {
        return;
      }

      event.preventDefault();

      if (timeoutClickRef.current)
      {
        clearTimeout(timeoutClickRef.current);
      }

      timeoutClickRef.current = setTimeout(() =>
      {
        config.functions?.setDashboard?.(dashboard);
        setToggleState(index);
        saveTabToStorage(index);
      }, 200);
    },
    [config, saveTabToStorage],
  );

  // Show rename dashboard
  const showRenameDashboard = useCallback(
    (_event: React.MouseEvent, dashboardId: number) =>
    {
      if (timeoutClickRef.current)
      {
        clearTimeout(timeoutClickRef.current);
      }

      const isAdmin = config.isAdmin ?? false;
      const updatedDashboards = pinedDashboards.map(dashboard =>
      {
        if (
          !dashboard.IsDefault &&
          dashboard.DashboardId === dashboardId &&
          (dashboard.IsDashboardCreator || isAdmin)
        )
        {
          return { ...dashboard, showEditName: true };
        }
        return { ...dashboard, showEditName: false };
      });

      setPinedDashboards(updatedDashboards);
    },
    [pinedDashboards],
  );

  // Save rename dashboard
  const saveRenameDashboard = useCallback(
    async (event: React.MouseEvent | React.KeyboardEvent, dashboardId: number, text: string) =>
    {
      const updatedDashboards = pinedDashboards.map(dashboard =>
      {
        if (dashboard.DashboardId === dashboardId)
        {
          return { ...dashboard, Name: text, showEditName: false, draftName: text };
        }
        return { ...dashboard, showEditName: false };
      });

      setPinedDashboards(updatedDashboards);

      const dashboard = updatedDashboards.find(d => d.DashboardId === dashboardId);
      if (dashboard)
      {
        await config.functions?.updateDashboard?.(event as React.MouseEvent, dashboard);
        toastSuccess('Dashboard saved successfully');
      }
    },
    [pinedDashboards, config],
  );

  // Close rename
  const closeRename = useCallback(() =>
  {
    const updatedDashboards = pinedDashboards.map(dashboard => ({
      ...dashboard,
      showEditName: false,
    }));
    setPinedDashboards(updatedDashboards);
  }, [pinedDashboards]);

  // Pin dashboard
  const pinDashboard = useCallback(
    (event: React.MouseEvent, dashboard: Dashboard) =>
    {
      // Functional setter avoids stale-closure issues on rapid successive clicks
      setPinedDashboards(prev =>
        prev.map(d =>
        {
          if (d.DashboardId === dashboard.DashboardId)
          {
            return {
              ...d,
              IsPineableDashboard: !dashboard.IsPineableDashboard,
              IsPinedDashboard: !dashboard.IsPinedDashboard,
            };
          }
          return d;
        }),
      );
      config.functions?.pinDashboard?.(event, dashboard);
    },
    [config],
  );

  // Open tab dashboard
  const openTabDashboard = useCallback(
    (event: React.MouseEvent, dashboard: Dashboard) =>
    {
      config.functions?.OpenTabDashboard?.(event, dashboard);

      const newPinned = [...pinedDashboards];
      const newNotPinned = notPinedDashboards.filter(d => d.DashboardId !== dashboard.DashboardId);

      dashboard.IsOpenDashboard = true;
      newPinned.push(dashboard);

      // Update order
      newPinned.forEach((d, index) =>
      {
        d.id = index;
        d.Order = index;
      });

      setPinedDashboards(newPinned);
      setNotPinedDashboards(newNotPinned);
      setToggleState(dashboard.DashboardId);
      config.functions?.setDashboard?.(dashboard);
    },
    [pinedDashboards, notPinedDashboards, config],
  );

  // Close modal for new dashboard
  const closeModalNewDashboard = useCallback(() =>
  {
    setShowConfirmationND(false);
  }, []);

  // Add dashboard (without default configuration)
  const addDashboard = useCallback(async () =>
  {
    setShowConfirmationND(false);
    const order = (notPinedDashboards?.length || 0) + (pinedDashboards?.length || 0) + 1;
    try
    {
      await dashboardService.AddDashboard({
        DashboardId: 0,
        Name: 'New Dashboard',
        IsPublic: false,
        Archived: false,
        PersonId: getPersonId(),
        ConfigValueId: config.configValueId ?? 1,
        ProjectId: config.projectId ?? null,
        GridSize: 4,
        DashboardViewTypeId: 1,
        IsFromDashboardGroup: false,
        Order: order,
        FakeDashboardType: 0,
      });
      reloadDashboard(true);
    }
    catch (error)
    {
      console.error('Error adding dashboard:', error);
    }
  }, [config, notPinedDashboards, pinedDashboards, reloadDashboard]);

  // Show modal for new dashboard
  const showModalNewDashboard = useCallback(
    async (_event: React.MouseEvent) =>
    {
      if (config.projectId)
      {
        try
        {
          const data = await dashboardApi.getProjectStatus(config.projectId);
          if (!Array.isArray(data) || data.length === 0)
          {
            addDashboard();
          }
          else
          {
            setShowConfirmationND(true);
          }
        }
        catch
        {
          addDashboard();
        }
      }
      else
      {
        addDashboard();
      }
    },
    [config, addDashboard],
  );

  // Add dashboard with default configuration
  const addDashboardDefaultConfiguration = useCallback(async () =>
  {
    const order = (notPinedDashboards?.length || 0) + (pinedDashboards?.length || 0) + 1;
    const request = {
      ConfigValueId: config.configValueId,
      ProjectId: config.projectId,
      Order: order,
    };

    setShowConfirmationND(false);

    try
    {
      await dashboardApi.addDashboardDefaultConfiguration(request);
      reloadDashboard(true);
    }
    catch (error)
    {
      console.error('Error adding dashboard with default configuration:', error);
    }
  }, [config, notPinedDashboards, pinedDashboards, reloadDashboard]);

  // Scroll left
  const scrollLft = useCallback(() =>
  {
    tabReactRef.current?.scrollBy({ left: -distance, behavior: 'smooth' });
  }, [distance]);

  // Scroll right
  const scrollRight = useCallback(() =>
  {
    tabReactRef.current?.scrollBy({ left: distance, behavior: 'smooth' });
  }, [distance]);

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) =>
  {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleDragCancel = useCallback(() =>
  {
    setActiveDragId(null);
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) =>
    {
      const { active, over } = event;
      setActiveDragId(null);

      if (over && active.id !== over.id)
      {
        const oldIndex = pinedDashboards.findIndex(d => d.DashboardId.toString() === active.id);
        const newIndex = pinedDashboards.findIndex(d => d.DashboardId.toString() === over.id);

        const reorderedItems = arrayMove(pinedDashboards, oldIndex, newIndex);

        // Update order
        reorderedItems.forEach((d, index) =>
        {
          d.Order = index;
        });

        suppressAutoScrollRef.current = true;
        setPinedDashboards(reorderedItems);

        // Debounce update position API call
        if (timeoutIdRef.current)
        {
          clearTimeout(timeoutIdRef.current);
        }

        timeoutIdRef.current = setTimeout(async () =>
        {
          const socketConnectionId = config.functions?.getSocketConnectionId?.() || '';
          const requestObj = {
            dashboards: reorderedItems,
            isFromProject: (config.projectId || 0) > 0,
            noNotification: false,
            socketConnectionId,
          };

          try
          {
            await dashboardApi.updatePosition(requestObj);
          }
          catch (error)
          {
            console.error('Error updating dashboard position:', error);
          }
        }, 200);
      }
    },
    [pinedDashboards, config],
  );

  // Move to modal for import from jira
  const ifjMoveToModal = useCallback(
    (index: number, dashboard: Dashboard) =>
    {
      setToggleState(index);
      config.functions?.setDashboard?.(dashboard);
    },
    [config],
  );

  // Clamp the drag preview to the visible tab strip
  const restrictToTabStrip = useCallback<Modifier>(
    ({ transform, draggingNodeRect }) =>
    {
      const container = tabReactRef.current;
      if (!container || !draggingNodeRect)
      {
        return transform;
      }

      const bounds = container.getBoundingClientRect();
      let x = transform.x;

      if (draggingNodeRect.left + x < bounds.left)
      {
        x = bounds.left - draggingNodeRect.left;
      }
      else if (draggingNodeRect.right + x > bounds.right)
      {
        x = bounds.right - draggingNodeRect.right;
      }

      return { ...transform, x, y: 0 };
    },
    [],
  );

  const dragModifiers = useMemo(
    () => [restrictToHorizontalAxis, restrictToTabStrip],
    [restrictToTabStrip],
  );

  const lastPinedDashboard = pinedDashboards[pinedDashboards.length - 1];
  const draggedDashboard = activeDragId
    ? pinedDashboards.find(d => d.DashboardId.toString() === activeDragId)
    : undefined;

  return (
    <div className="tabs-container">
      {showArrows && (
        <div
          className="react-dashboard-left-arrow"
          onClick={scrollLft}
        >
          <i className="fa fa-chevron-left" style={{ visibility: styleInitialNoMore ? 'hidden' : 'visible' }} aria-hidden="true"></i>
        </div>
      )}

      <div
        ref={tabReactRef}
        className="tabreact"
        style={{ opacity: tabsReady ? 1 : 0, transition: tabsReady ? 'opacity 120ms ease-in' : 'none' }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={dragModifiers}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={pinedDashboards.map(d => d.DashboardId.toString())}
            strategy={horizontalListSortingStrategy}
          >
            <div className="tab-react-control dl-list">
              {pinedDashboards.map(item => (
                <SortableTabItem
                  key={item.DashboardId}
                  item={item}
                  toggleState={toggleState}
                  config={config}
                  isHighlighted={highlightedTabIds.has(item.DashboardId)}
                  onToggleTab={toggleTab}
                  onShowRenameDashboard={showRenameDashboard}
                  onPinDashboard={pinDashboard}
                  onOpenTabDashboard={openTabDashboard}
                  scrollRight={scrollRight}
                  saveRenameDashboard={saveRenameDashboard}
                  closeRename={closeRename}
                  ifjMoveToModal={ifjMoveToModal}
                  dashboards={dashboards}
                  setPinedDashboards={setPinedDashboards}
                  setNotPinedDashboards={setNotPinedDashboards}
                  tabsToShow={pinedDashboards}
                  onSelectDashboard={d =>
                    toggleTab(new Event('click') as any, d.DashboardId, d, true)
                  }
                />
              ))}

              {/* Outside the sortable items so reordering never drags them along */}
              {lastPinedDashboard && !config.isOrganize && !config.isBookmarks && (
                <div className="nodiv-tabs">
                  <ActionMenuTab
                    typeMenu="2"
                    openTabDashboard={openTabDashboard}
                    functions={config.functions}
                    config={config}
                    canEdit={config.canEdit}
                    isAdmin={config.isAdmin}
                    api={config.api}
                    dashboard={lastPinedDashboard}
                    notPinedDashboards={notPinedDashboards}
                    ifjMoveToModal={ifjMoveToModal}
                    dashboards={dashboards}
                    onToggleTab={toggleTab}
                    setPinedDashboards={setPinedDashboards}
                    setNotPinedDashboards={setNotPinedDashboards}
                    activeDashboard={toggleState}
                    tabsToShow={pinedDashboards}
                    onSelectDashboard={d =>
                      toggleTab(new Event('click') as any, d.DashboardId, d, true)
                    }
                    onDashboardUpdated={config.functions?.onDashboardUpdated}
                  />
                  <span
                    className="add-new-dashboard-button-top-tabs enable-help-tooltip"
                    data-title="Add New Dashboard"
                    onClick={showModalNewDashboard}
                  ></span>
                </div>
              )}
            </div>
          </SortableContext>

          {/* Drag preview: the tab only */}
          <DragOverlay dropAnimation={null}>
            {draggedDashboard && (
              <ul className="tab-container tab-drag-preview">
                <li className="tab-item">
                  <div className="tab-title">{draggedDashboard.Name}</div>
                  <span style={{ paddingRight: '9px' }}></span>
                </li>
              </ul>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {showArrows && (
        <div
          className="react-dashboard-right-arrow"
          onClick={scrollRight}
        >
          <i className="fa fa-chevron-right" style={{ visibility: styleFinalNoMore ? 'hidden' : 'visible' }} aria-hidden="true"></i>
        </div>
      )}

      <Modal open={showConfirmationND} onClose={closeModalNewDashboard} title="New Dashboard">
        <div>
          <p className="pt-[10px] pb-[8px] px-[20px] text-sm">
            Do you want to use the default configuration to create the new dashboard?
          </p>
          <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
            <button
              type="button"
              onClick={addDashboardDefaultConfiguration}
              className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={addDashboard}
              className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
            >
              No
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TabControl;
